import type {
  ChatHistoryItem,
  StreamChunk,
  FinalResponse,
  UserInfo,
} from "./type";

export const SUGGESTION_CARDS = [
  {
    title: "Education",
    description: "화성에서 생명체가 살 수 있을까요?",
    titleColor: "#6d2ef2",
  },
  {
    title: "Research",
    description: "가장 큰 소수는 왜 찾는 걸까요?",
    titleColor: "#42ACBB",
  },
  {
    title: "Mock interview",
    description: "우주의 끝은 있나요?",
    titleColor: "#FF4084",
  },
  {
    title: "News",
    description: "촛불은 불면 꺼지는데 산불은 바람에 왜 번지나요?",
    titleColor: "#417AF5",
  },
] as const;

// ============================================
// 더미 채팅 히스토리 데이터
// ============================================

/**
 * 테스트용 채팅 히스토리
 * 실제 대화 시나리오를 반영한 샘플 데이터
 */
export const MOCK_CHAT_HISTORY: ChatHistoryItem[] = [
  {
    id: "1704067200000",
    response_id: "resp_1704067200000",
    input_text: "광합성에 대해 설명해줘",
    response: `# 광합성이란?

**광합성**은 식물, 조류, 일부 박테리아가 **빛 에너지를 화학 에너지로 전환**하는 과정입니다.

## 주요 과정

광합성은 크게 두 단계로 나뉩니다:

1. **명반응 (Light-dependent reactions)**
   - 엽록체의 틸라코이드 막에서 일어남
   - 빛 에너지를 흡수하여 ATP와 NADPH 생성
   - 물을 분해하여 산소 방출

2. **암반응 (Light-independent reactions) / 캘빈 회로**
   - 엽록체의 스트로마에서 일어남
   - CO₂를 고정하여 포도당 합성
   - ATP와 NADPH를 사용

## 전체 화학식

\`\`\`
6CO₂ + 6H₂O + 빛 에너지 → C₆H₁₂O₆ + 6O₂
\`\`\`

이 과정을 통해 식물은 생명 활동에 필요한 에너지를 얻고, 지구상의 모든 생명체에게 산소를 공급합니다.`,
    action: "chat",
    reference: [
      {
        url: "https://www.dongascience.com/news/view/58901",
        name: "광합성의 비밀, 엽록소의 역할",
        medium: "과학동아",
        summary:
          "식물의 광합성 과정에서 엽록소가 어떻게 빛을 흡수하고 에너지를 전환하는지 상세히 설명합니다. 엽록체의 구조와 명반응, 암반응의 메커니즘을 이해하기 쉽게 풀어냅니다.",
        image: [
          {
            IMG_SRC:
              "https://img.dongascience.com/photo/2020/12/photosynthesis_01.jpg",
            IMG_TITLE: "광합성 과정 도식",
            IMG_Description: "명반응과 암반응의 전체 과정을 나타낸 도식",
          },
          {
            IMG_SRC:
              "https://img.dongascience.com/photo/2020/12/chloroplast.jpg",
            IMG_TITLE: "엽록체 구조",
            IMG_Description: "틸라코이드와 스트로마의 구조",
          },
        ],
      },
      {
        url: "https://www.dongascience.com/news/view/58902",
        name: "식물의 에너지 생산 공장, 엽록체",
        medium: "과학동아",
        summary:
          "엽록체의 구조와 기능, 그리고 광합성이 일어나는 메커니즘을 다룹니다. 틸라코이드 막과 스트로마에서 일어나는 반응을 자세히 설명합니다.",
        image: [
          {
            IMG_SRC: "https://img.dongascience.com/photo/2020/12/plant_cell.jpg",
            IMG_TITLE: "식물 세포",
            IMG_Description: "엽록체가 포함된 식물 세포의 단면도",
          },
        ],
      },
    ],
    images: [],
    recommended_questions: [
      "광합성은 어떤 파장의 빛을 가장 잘 흡수하나요?",
      "엽록소 a와 b의 차이는 무엇인가요?",
      "겨울철에도 광합성이 일어나나요?",
    ],
    latency: 2.5,
    token_count: 350,
    user_eva: {
      simple_eva: null,
    },
    isShowDetail: false,
    is_open: true,
  },
  {
    id: "1704067260000",
    response_id: "resp_1704067260000",
    input_text: "광합성은 어떤 파장의 빛을 가장 잘 흡수하나요?",
    response: `좋은 질문이에요! 엽록소는 특정 파장의 빛을 선택적으로 흡수합니다.

## 엽록소의 빛 흡수 스펙트럼

엽록소는 **청색광(450nm)**과 **적색광(680nm)**을 가장 잘 흡수합니다.

반면 **녹색광(550nm)**은 거의 흡수하지 않고 반사하기 때문에, 식물의 잎이 녹색으로 보이는 것입니다.

### 파장별 흡수율

| 파장 영역 | 파장 범위 | 흡수율 | 색상 |
|---------|----------|-------|------|
| 청색광 | 400-500nm | 높음 | 파란색 |
| 녹색광 | 500-600nm | 낮음 | 녹색 |
| 적색광 | 600-700nm | 높음 | 빨간색 |

## 실생활 응용

이러한 특성 때문에 **식물 재배용 LED**는 주로 청색과 적색 LED를 조합하여 사용합니다. 에너지 효율이 높고 광합성 효율도 극대화할 수 있기 때문이죠!`,
    action: "chat",
    reference: [
      {
        url: "https://www.dongascience.com/news/view/58903",
        name: "빛의 파장과 광합성 효율",
        medium: "과학동아",
        summary:
          "다양한 파장의 빛이 광합성에 미치는 영향을 실험 데이터와 함께 분석합니다. 엽록소 a와 b의 흡수 스펙트럼 차이도 설명합니다.",
        image: [
          {
            IMG_SRC: "https://img.dongascience.com/photo/2020/12/spectrum.jpg",
            IMG_TITLE: "흡수 스펙트럼",
            IMG_Description: "엽록소 a와 b의 빛 흡수 스펙트럼 그래프",
          },
        ],
      },
    ],
    images: [],
    recommended_questions: [
      "LED 식물등을 선택할 때 어떤 점을 고려해야 하나요?",
      "자외선이나 적외선도 광합성에 영향을 주나요?",
      "식물마다 선호하는 빛의 파장이 다른가요?",
    ],
    latency: 1.8,
    token_count: 280,
    user_eva: {
      simple_eva: 1,
      eva: "이해하기 쉽게 표로 정리해주셔서 좋았어요!",
    },
    isShowDetail: false,
    is_open: true,
  },
  {
    id: "1704067320000",
    response_id: "resp_1704067320000",
    input_text: "블랙홀은 정말 검은색인가요?",
    response: `재미있는 질문이네요! 실제로 블랙홀은 우리가 생각하는 것과 조금 다릅니다.

## 블랙홀의 색깔

엄밀히 말하면, 블랙홀 자체는 **색깔이 없습니다**. 빛조차 빠져나올 수 없기 때문에 '검은색'으로 보이는 것이죠.

하지만 블랙홀 주변은 매우 밝고 화려합니다!

### 강착원반 (Accretion Disk)

블랙홀 주변으로 빨려 들어가는 물질들이 만드는 **강착원반**은:
- 초고온 상태 (수백만~수억 도)
- 강렬한 X선과 가시광선 방출
- 주황색, 노란색, 파란색 등 다양한 색으로 빛남

### 사건의 지평선 (Event Horizon)

블랙홀의 경계인 사건의 지평선은 실제로 완전히 검은색입니다. 이 경계를 넘으면 빛도 탈출할 수 없기 때문이죠.

2019년에 촬영된 M87 블랙홀의 이미지를 보면, 중앙의 검은 원(그림자)과 주변의 밝은 고리를 확인할 수 있습니다!`,
    action: "chat",
    reference: [
      {
        url: "https://www.dongascience.com/news/view/58904",
        name: "블랙홀의 첫 사진, M87",
        medium: "과학동아",
        summary:
          "2019년 인류 최초로 촬영된 블랙홀 이미지의 의미와 촬영 과정을 상세히 다룹니다. 사건망원경(EHT) 프로젝트의 성과를 소개합니다.",
        image: [
          {
            IMG_SRC: "https://img.dongascience.com/photo/2019/04/m87_bh.jpg",
            IMG_TITLE: "M87 블랙홀",
            IMG_Description: "인류 최초로 촬영된 블랙홀의 이미지",
          },
        ],
      },
      {
        url: "https://www.dongascience.com/news/view/58905",
        name: "강착원반의 비밀",
        medium: "과학동아",
        summary:
          "블랙홀 주변의 강착원반에서 일어나는 물리 현상을 설명합니다. 고온의 플라즈마가 어떻게 빛을 방출하는지 알아봅니다.",
      },
    ],
    images: [],
    recommended_questions: [
      "블랙홀에 빨려들어가면 어떻게 되나요?",
      "화이트홀도 존재하나요?",
      "블랙홀은 언젠가 사라지나요?",
    ],
    latency: 3.2,
    token_count: 420,
    user_eva: {
      simple_eva: null,
    },
    isShowDetail: false,
    is_open: true,
  },
];

// ============================================
// 스트리밍 데이터
// ============================================

/**
 * 스트리밍 텍스트 청크 예시
 * 실시간 타이핑 효과 테스트용
 */
export const MOCK_STREAM_CHUNKS: StreamChunk[] = [
  { type: "main", text: "광합성은 " },
  { type: "main", text: "**식물, 조류, 일부 박테리아**가 " },
  { type: "main", text: "빛 에너지를 " },
  { type: "main", text: "화학 에너지로 전환하는 과정입니다.\n\n" },
  { type: "DONE" },
  { type: "sub", text: "## 주요 과정\n\n" },
  { type: "main", text: "1. **명반응**: " },
  { type: "main", text: "빛을 흡수하여 ATP와 NADPH 생성\n" },
  { type: "main", text: "2. **암반응**: " },
  { type: "main", text: "CO₂를 고정하여 포도당 합성\n\n" },
  { type: "FINISH" },
];

/**
 * API 최종 응답 예시
 */
export const MOCK_FINAL_RESPONSE: FinalResponse = {
  type: "all",
  response_id: "resp_1704067200000",
  input_text: "광합성에 대해 설명해줘",
  response: `# 광합성이란?

**광합성**은 식물, 조류, 일부 박테리아가 **빛 에너지를 화학 에너지로 전환**하는 과정입니다.

## 주요 과정

광합성은 크게 두 단계로 나뉩니다:

1. **명반응 (Light-dependent reactions)**
   - 엽록체의 틸라코이드 막에서 일어남
   - 빛 에너지를 흡수하여 ATP와 NADPH 생성
   - 물을 분해하여 산소 방출

2. **암반응 (Light-independent reactions) / 캘빈 회로**
   - 엽록체의 스트로마에서 일어남
   - CO₂를 고정하여 포도당 합성
   - ATP와 NADPH를 사용

## 전체 화학식

\`\`\`
6CO₂ + 6H₂O + 빛 에너지 → C₆H₁₂O₆ + 6O₂
\`\`\`

이 과정을 통해 식물은 생명 활동에 필요한 에너지를 얻고, 지구상의 모든 생명체에게 산소를 공급합니다.`,
  action: "chat",
  reference: [
    {
      url: "https://www.dongascience.com/news/view/58901",
      name: "광합성의 비밀, 엽록소의 역할",
      medium: "과학동아",
      summary:
        "식물의 광합성 과정에서 엽록소가 어떻게 빛을 흡수하고 에너지를 전환하는지 상세히 설명합니다.",
      image: [
        {
          IMG_SRC:
            "https://img.dongascience.com/photo/2020/12/photosynthesis_01.jpg",
          IMG_TITLE: "광합성 과정 도식",
        },
      ],
    },
  ],
  images: [],
  recommended_questions: [
    "광합성은 어떤 파장의 빛을 가장 잘 흡수하나요?",
    "엽록소 a와 b의 차이는 무엇인가요?",
    "겨울철에도 광합성이 일어나나요?",
  ],
  latency: 2.5,
  token_count: 350,
  user_eva: {
    simple_eva: null,
  },
};

// ============================================
// 사용자 데이터
// ============================================

/**
 * 테스트용 사용자 정보
 */
export const MOCK_USER_INFO: UserInfo = {
  id: 12345,
  name: "홍길동",
  tone: "선생님 말투",
  picture_extension: null,
  picture_data: null,
  paid: true,
  start_dt: "2025-01-01",
  end_dt: "2025-12-31",
  tutor_cnt: 50,
  socra_cnt: 30,
};
